<script setup>
/**
 * 問題詳情元件 (QusDetail.vue)
 *
 * 顯示單一問題內容與所有回覆，提供回覆、編輯 / 刪除問題與回覆、
 * 對問題 / 回覆按讚、設定最佳解答，以及複製問題連結等功能。
 *
 * @fires changeView - 返回列表或切換畫面
 */
import { ref, defineEmits, defineProps, onMounted, onUnmounted } from 'vue';
import questionsAPI from '../apis/questions';
import ClipboardJS from 'clipboard';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

// 因為改變 delimiters 出現的問題
// eslint-disable-next-line no-unused-vars
import { getDiff, questionType } from '../utils/format';
// eslint-disable-next-line no-unused-vars
import { toolbarOptions, modulesOptions } from '../plugins/quill';
import { useToast } from '../composables/useToast';
import { useSession } from '../composables/useSession';
import EditQuestion from './Detail/EditQuestion.vue';

const { postMessage } = useToast();
const { actionPermission } = useSession();

const emit = defineEmits(['changeView']);
const props = defineProps({
  id: {
    type: Number,
    default: null,
  },
  token: {
    type: String,
    default: '',
  },
  isLink: {
    type: Boolean,
    default: false,
  },
});
const question = ref({});
const isLoading = ref(false);
isLoading.value = true;
/**
 * 取得單一問題詳細內容並寫入 `question`。
 * @param {number} id - 問題 id
 */
const getQuestionDetail = (id) => {
  questionsAPI.apiGetQuestion(id).then((res) => {
    question.value = res.data.question;
    isLoading.value = false;
  }).catch((error) => {
    isLoading.value = false;
    postMessage('danger', '取得問題詳細內容時發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  });
};
const currentUrl = ref('');
onMounted(() => {
  new ClipboardJS('.hexschool-copy');
  getQuestionDetail(props.id);
  currentUrl.value = window.location.href;
});

const replayIsLoading = ref(false); // 回覆區塊的 Loading
const questionEditStatus = ref(false); // 問題是否有變動
// 回覆問題
const editor = ref(null);
const replyContent = ref('');
/**
 * 送出對問題的新回覆，成功後清空編輯器並重新取得問題詳情。
 */
const replyQuestion = () => {
  replayIsLoading.value = true;
  questionsAPI
    .apiPostReply(props.id, replyContent.value)
    .then(() => {
      editor.value.setHTML('');
      replayIsLoading.value = false;
      getQuestionDetail(props.id);
      questionEditStatus.value = true;
    });
};
// 編輯回覆
const editReply = ref({
  show: false,
  replyId: null,
  body: '',
});
const closeEditReply = () => {
  editReply.value = {
    show: false,
    replyId: null,
    body: '',
  };
};
/**
 * 開啟指定回覆的編輯狀態，帶入其 id 與內容。
 * @param {object} reply - 欲編輯的回覆物件
 */
const startEditReply = (reply) => {
  editReply.value.show = true;
  editReply.value.replyId = reply.id;
  editReply.value.body = reply.body;
};
/**
 * 送出回覆編輯內容，成功後關閉編輯狀態並重新取得問題詳情。
 */
const submitEditReply = () => {
  replayIsLoading.value = true;
  questionsAPI
    .apiPutReply(props.id, editReply.value.replyId, editReply.value.body)
    .then(() => {
      replayIsLoading.value = false;
      editReply.value = {
        show: false,
        replyId: null,
        body: '',
      };
      getQuestionDetail(props.id);
    });
};
// 刪除回覆
/**
 * 二次確認後刪除指定回覆，成功則重新取得問題詳情。
 * @param {number} id - 回覆 id
 */
const deleteReply = (id) => {
  replayIsLoading.value = true;
  const text = '確認刪除這則回覆，刪除後就無法恢復。';
  if (confirm(text) === true) {
    questionsAPI.apiDeleteReply(props.id, id).then((res) => {
      if (res.data.message === '成功') {
        getQuestionDetail(props.id);
        replayIsLoading.value = false;
      } else {
        replayIsLoading.value = false;
        alert('刪除回覆失敗。');
      }
    });
  } else {
    replayIsLoading.value = false;
  }
};

// 編輯問題
const editState = ref({
  show: false,
  data: {},
});
/**
 * 關閉問題編輯畫面；若 `refresh` 為 true 則重新取得問題詳情。
 * @param {boolean} refresh - 是否需要重新載入問題
 */
const closeEdit = (refresh) => {
  editState.value.show = false;
  if (refresh) {
    getQuestionDetail(props.id);
    questionEditStatus.value = true;
  }
};
// 操作選單（編輯／刪除／最佳解答）的開關，以 Vue 控制而非 Bootstrap 的 document data-api。
// 嵌在 Teachable 頁面時，頁面上可能存在第二份 Bootstrap，兩份都綁 data-bs-toggle 會把同一次
// 點擊 toggle 兩次而互相抵銷，導致選單打不開；改由這裡切換 .show 即可完全避開該衝突。
// openMenuId：null 表示全關；'question' 為主問題選單；'reply-<id>' 為各回覆選單（同時只開一個）。
const openMenuId = ref(null);
const toggleMenu = (id) => {
  openMenuId.value = openMenuId.value === id ? null : id;
};
const closeMenus = () => {
  openMenuId.value = null;
};
// 點擊選單以外的任何地方即關閉（toggle 按鈕以 @click.stop 阻止冒泡，故開啟當下不會被這裡關掉）
onMounted(() => {
  document.addEventListener('click', closeMenus);
});
onUnmounted(() => {
  document.removeEventListener('click', closeMenus);
});

/**
 * 開啟問題編輯畫面並帶入問題內容。
 * @param {object} questionContent - 欲編輯的問題物件
 */
const editQuestion = (questionContent) => {
  editState.value.show = true;
  editState.value.data = questionContent;
};
// 刪除問題
/**
 * 二次確認後刪除問題，成功則返回列表並要求重新整理。
 * @param {number} id - 問題 id
 */
const deleteQuestion = (id) => {
  const text = '確認刪除這則問題，刪除後就無法恢復。';
  if (confirm(text) === true) {
    questionsAPI.apiDeleteQuestion(id).then((res) => {
      if (res.data.message === '成功') {
        emit('changeView', { type: 'list', id: -1, refresh: true });
      } else {
        alert('刪除問題失敗。');
      }
    });
  }
};

// 對問題按讚、取消讚
/**
 * 對問題按讚或取消讚，並就地更新 `question` 的讚數與狀態。
 * @param {'post'|'delete'} method - post 按讚、delete 取消讚
 * @param {number} qId - 問題 id
 */
const questionUpvote = (method, qId) => {
  const result = method === 'post';
  questionsAPI.apiToggleQuestionUpvote(method, qId).then((res) => {
    if (res.data.message === '成功') {
      if (result) {
        question.value.upvotes += 1;
      } else {
        question.value.upvotes -= 1;
      }
      question.value.is_upvote = result;
      questionEditStatus.value = true;
    }
  });
};
// 對回覆按讚、取消讚
/**
 * 對回覆按讚或取消讚，並就地更新該回覆的讚數與狀態。
 * @param {'post'|'delete'} method - post 按讚、delete 取消讚
 * @param {number} rId - 回覆 id
 */
const replyUpvote = (method, rId) => {
  const result = method === 'post';
  const findIndex = question.value.replies.findIndex((r) => r.id === rId);
  questionsAPI.apiToggleReplyUpvote(method, props.id, rId).then((res) => {
    if (res.data.message === '成功') {
      if (result) {
        question.value.replies[findIndex].upvotes += 1;
      } else {
        question.value.replies[findIndex].upvotes -= 1;
      }
      question.value.replies[findIndex].is_upvote = result;
    }
  });
};

// 設定最佳解答
/**
 * 設定或取消回覆的最佳解答標記。
 * @param {'post'|'delete'} method - post 設為最佳解答、delete 取消
 * @param {number} rId - 回覆 id
 */
const bestAnswer = (method, rId) => {
  const result = method === 'post';
  const findIndex = question.value.replies.findIndex((r) => r.id === rId);
  questionsAPI.apiToggleReplyBestAnswer(method, props.id, rId).then((res) => {
    if (res.data.message === '成功') {
      if (result) {
        question.value.replies[findIndex].is_best_answer += 1;
      } else {
        question.value.replies[findIndex].is_best_answer -= 1;
      }
    }
  }).catch(err => {
    postMessage('danger', '最佳解答設定失敗', `${err.response.data.message}`);
  });
};
</script>

<template>
  <p>
    <a href="#" class="text-dark text-decoration-none d-flex align-items-center" @click.prevent="
      $emit('changeView', { type: 'list', id: -1, refresh: questionEditStatus ? true : false })
      "><span class="material-icons align-bottom me-1"> arrow_back </span>
      返回問題列表</a>
  </p>
  <!-- 主問題 -->
  <div class="border border-dark p-3 my-3 position-relative" style="min-height: 200px">
    <loading :active="isLoading" :is-full-page="false" />
    <div v-if="editState.show" class="">
      <EditQuestion :data="editState.data" @close-edit="closeEdit" />
    </div>
    <div v-else-if="question.title" class="d-flex align-items-start">
      <img :src="question.avatar ||
      'https://s.gravatar.com/avatar/9024444d63ae53512c40d2a60da3d6a3?d=mm'
      " alt="" width="48" height="48" style="object-fit: cover" class="rounded-circle">
      <div class="mt-2 mx-3 w-75">
        <h5 class="mb-1 hexschool-copy" title="點一下複製問題連結" style="cursor: pointer;"
          :data-clipboard-text="`${currentUrl}?questions_id=${question.id}`">
          ${question.title}
        </h5>
        <small class="text-muted">${question.nickname} / ${getDiff(question.create_time)} /
          ${question.video_course_lecture_name} /
          ${questionType(question.question_type_name)}</small>
        <div class="mt-4 hexschool-q-a-content">
          <div v-html="question.body" />
        </div>
      </div>
      <div class="ms-auto">
        <div class="d-flex">
          <small class="d-flex align-items-center me-2">
            ${question.upvotes}
            <span v-if="!actionPermission || question.from === 'udemy'"
              class="ms-1 material-icons">thumb_up_off_alt</span>
            <a v-else-if="!question.is_upvote" href="#" class="link-dark"
              @click.prevent="questionUpvote('post', question.id)">
              <span class="ms-1 material-icons"> thumb_up_off_alt </span>
            </a>
            <a v-else href="#" class="link-dark" @click.prevent="questionUpvote('delete', question.id)">
              <span class="ms-1 material-icons-sharp">
                thumb_up_off_alt
              </span>
            </a>
          </small>
          <small class="d-flex align-items-center">
            ${question.replies.length} <span class="ms-1 material-icons"> chat_bubble_outline </span>
          </small>
          <div
            v-if="actionPermission && token && question.is_author && (question.replies && question.replies.length === 0) && question.from !== 'udemy'"
            class="btn-group position-relative ms-2">
            <button type="button" class="btn btn-sm btn-hex-secondary"
              :aria-expanded="openMenuId === 'question'" @click.stop="toggleMenu('question')">
              ...
            </button>
            <ul class="dropdown-menu dropdown-menu-end hex-dropdown-transform-none" data-bs-popper="static"
              :class="{ show: openMenuId === 'question' }">
              <li class="hex-dropdown-item-margin-0">
                <button class="dropdown-item" type="button" @click="editQuestion(question)">
                  編輯
                </button>
              </li>
              <li class="hex-dropdown-item-margin-0">
                <button class="dropdown-item" type="button" @click="deleteQuestion(question.id)">
                  刪除
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- 回覆 -->
  <p v-if="question.replies" class="text-muted">
    共有 ${ question.replies.length } 則回應
  </p>
  <div class="position-relative">
    <loading :active="replayIsLoading" :is-full-page="false" />
    <div v-for="reply in question.replies" :key="reply.id" class="p-3 border-bottom"
      :class="{ 'bg-light': reply.is_assistant_reply || reply.assistant_title }">
      <div class="d-flex align-items-start">
        <img :src="reply.avatar ||
      'https://s.gravatar.com/avatar/9024444d63ae53512c40d2a60da3d6a3?d=mm'
      " alt="" width="48" height="48" style="object-fit: cover" class="rounded-circle">
        <div class="mt-2 mx-3 w-75">
          <h5 class="mb-1">
            ${ reply.nickname }
            <small :style="{ color: reply.assistant_color_code }">${ reply.assistant_title }</small>
          </h5>
          <small class="text-muted">${getDiff(reply.create_time)}</small>
          <div v-if="reply.is_best_answer === 1" class="my-2">
            <span class="p-1 bg-hex-warning" style="font-size: 14px;">標記最佳解答</span>
          </div>
          <div class="mt-4 hexschool-q-a-content">
            <!-- 回覆編輯 -->
            <div v-if="editReply.show && editReply.replyId === reply.id" class="">
              <QuillEditor v-model:content="editReply.body" theme="snow" content-type="html" :toolbar="toolbarOptions"
                :modules="modulesOptions" />
              <div class="my-3 d-flex justify-content-between">
                <button class="btn btn-light px-4" @click="closeEditReply">
                  取消
                </button>
                <button class="btn btn-hex-primary px-4" :disabled="editReply.body === '' || editReply.body === '<p><br></p>' || isLoading || replayIsLoading" @click="submitEditReply">
                  送出
                </button>
              </div>
            </div>
            <!-- 回覆編輯 end -->
            <div v-else v-html="reply.body" />
          </div>
        </div>
        <div class="ms-auto text-nowrap">
          <div class="d-flex align-items-center">
            <small class="d-flex align-items-center">
              ${reply.upvotes}
              <span v-if="!actionPermission || question.from === 'udemy'"
                class="ms-1 material-icons">thumb_up_off_alt</span>
              <a v-else-if="!reply.is_upvote" href="#" class="link-dark" @click.prevent="replyUpvote('post', reply.id)">
                <span class="ms-1 material-icons"> thumb_up_off_alt </span>
              </a>
              <a v-else href="#" class="link-dark" @click.prevent="replyUpvote('delete', reply.id)">
                <span class="ms-1 material-icons-sharp">
                  thumb_up_off_alt
                </span>
              </a>
            </small>
            <div v-if="question.from !== 'udemy' && ((question.is_author && (reply.is_assistant_reply || reply.assistant_title)) || reply.is_author) && actionPermission" class="btn-group position-relative ms-2">
              <button type="button" class="btn btn-sm btn-hex-secondary"
                :aria-expanded="openMenuId === 'reply-' + reply.id" @click.stop="toggleMenu('reply-' + reply.id)">
                ...
              </button>
              <ul class="dropdown-menu dropdown-menu-end hex-dropdown-transform-none" data-bs-popper="static"
                :class="{ show: openMenuId === 'reply-' + reply.id }">
                <li v-if="question.is_author && (reply.is_assistant_reply || reply.assistant_title)"
                  class="hex-dropdown-item-margin-0">
                  <button v-if="reply.is_best_answer === 0" class="dropdown-item" type="button"
                    @click.prevent="bestAnswer('post', reply.id)">
                    最佳解答
                  </button>
                  <button v-else class="dropdown-item" type="button" @click.prevent="bestAnswer('delete', reply.id)">
                    取消最佳解答
                  </button>
                </li>
                <li v-if="reply.is_author" class="hex-dropdown-item-margin-0">
                  <button class="dropdown-item" type="button" @click="startEditReply(reply)">
                    編輯
                  </button>
                </li>
                <li v-if="reply.is_author" class="hex-dropdown-item-margin-0">
                  <button class="dropdown-item" type="button" @click="deleteReply(reply.id)">
                    刪除
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 回覆 end-->
  <!-- Editor -->
  <div class="mt-3">
    <div v-if="question.from === 'udemy'">
      <p class="text-center text-muted">此問題來自 Udemy，無法進行回覆</p>
    </div>
    <div v-else-if="!props.isLink">
      <p class="text-center text-muted">請先綁定 RPG 帳號後，即可回覆此問題</p>
    </div>
    <div v-else-if="actionPermission">
      <QuillEditor ref="editor" v-model:content="replyContent" theme="snow" content-type="html"
        :toolbar="toolbarOptions" :modules="modulesOptions" />
      <div class="my-3 d-flex justify-content-end">
        <button class="btn btn-hex-primary px-4"
          :disabled="replyContent === '' || isLoading || replayIsLoading || !actionPermission" @click="replyQuestion">
          送出
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped></style>

<spec lang="md">
# 問題詳情 (QusDetail.vue)

## 元件概述
- **用途**：顯示單一問題與其所有回覆，提供回覆、編輯 / 刪除問題與回覆、按讚、設定最佳解答與複製問題連結。

## Props
| 名稱 | 類型 | 必填 | 預設值 | 說明 |
|-----|------|-----|--------|------|
| id | `number` | 否 | `null` | 問題 id，`onMounted` 時據此載入詳情 |
| token | `string` | 否 | `''` | 使用者 token，判斷可否顯示操作選單 |
| isLink | `boolean` | 否 | `false` | 是否已綁定 RPG 帳號，未綁定不可回覆 |

## Emits
| 事件名稱 | 參數 | 說明 |
|---------|-----|------|
| changeView | `({ type, id, refresh })` | 返回列表；`refresh` 依是否編輯過而定 |

## Slots
無

## 功能行為
- `onMounted` 初始化 `ClipboardJS('.hexschool-copy')`、載入問題詳情、記錄 `currentUrl`
- 點擊標題可複製問題連結（`?questions_id=<id>`）
- 回覆：`replyQuestion` 送出新回覆；`startEditReply` / `submitEditReply` 編輯回覆；`deleteReply` 二次確認後刪除
- 問題：`editQuestion` 開啟 `EditQuestion` 子元件編輯；`deleteQuestion` 二次確認後刪除並返回列表
- 按讚：`questionUpvote` / `replyUpvote` 就地更新讚數與 `is_upvote`
- 最佳解答：`bestAnswer` 設定 / 取消回覆的最佳解答標記
- 有任何編輯 / 按讚 / 回覆時，`questionEditStatus` 設為 true，返回列表時要求 refresh
- 回覆區依 `question.from`、`isLink`、`actionPermission` 顯示 Udemy 提示、綁定提示或編輯器

## 測試行為
- 應顯示問題標題、作者、分類與內容
- 問題來自 Udemy（`question.from === 'udemy'`）時，應顯示「無法回覆」提示且不顯示按讚連結
- 未綁定（`isLink` 為 false）時，回覆區應顯示「請先綁定 RPG 帳號」提示
- 具操作權限且已綁定時，應顯示回覆編輯器與送出按鈕
- 點擊按讚時，應呼叫對應 API 並更新讚數
- 作者且回覆數為 0 時，問題應顯示編輯 / 刪除選單
- 刪除問題 / 回覆時，應先 `confirm` 再呼叫刪除 API
- 設定最佳解答成功時，該回覆應顯示「標記最佳解答」
- 返回列表時，應 emit `changeView`，`refresh` 依 `questionEditStatus` 決定

## UI 規格
- 主問題以 `.border.border-dark` 卡片呈現，最小高度 200px
- 助教回覆（`is_assistant_reply` / `assistant_title`）套用 `bg-light` 背景
- 最佳解答標記使用 `.bg-hex-warning` 標籤
- 使用 `QuillEditor`（snow 主題、html 內容）編輯回覆
- 各載入狀態以 `vue-loading-overlay` 顯示區塊級 Loading
- 操作選單（編輯／刪除／最佳解答）以 Vue 控制開關（`openMenuId` + `toggleMenu`，切換 `.dropdown-menu` 的 `.show`），不使用 Bootstrap 的 document data-api，避免嵌入 Teachable 時第二份 Bootstrap 造成雙重 toggle 抵銷；定位改由靜態 `data-bs-popper="static"` 讓 Bootstrap CSS 生效
</spec>
